import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Button,
  Card,
  CardContent,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  Stack,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import { getBankerDocumentsByBanker } from '../../../services/banker.service';
import { uploadCustomerDocument } from '../../../services/customer.service';
import { useAuth } from '../../../hook/userRoleHook';
import { ROLES } from '../../../constants/general.constant';

interface BankerDocumentsViewProps {
  dealId: string;
  bankerId: string;
  bankerName: string;
  onViewTermSheet: (bankerId: string) => void;
}

interface BankerDocument {
  id: string;
  docName: string;
  filePath?: string;
  originalName?: string;
  customerFilePath?: string;
  customerOriginalName?: string;
  isCustomerUpload: boolean;
  createdAt: string;
}

const MAX_FILE_SIZE = 25 * 1024 * 1024;
const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];

const BankerDocumentsView: React.FC<BankerDocumentsViewProps> = ({
  dealId,
  bankerId,
  bankerName,
  onViewTermSheet,
}) => {
  const { role } = useAuth();
  const [documents, setDocuments] = useState<BankerDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<BankerDocument | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const canCustomerUpload = role === ROLES.CUSTOMER;
  const requestedCount = documents.filter((doc) => doc.isCustomerUpload).length;
  const completedCount = documents.filter((doc) => doc.customerFilePath).length;
  const bankerFileCount = documents.filter((doc) => doc.filePath).length;

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await getBankerDocumentsByBanker(dealId, bankerId);
      setDocuments(response || []);
      setError(null);
    } catch (err: any) {
      setError(err?.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, bankerId]);

  const showFileError = (message: string) => {
    setSnackbar({
      open: true,
      message,
      severity: 'error',
    });
  };

  const setSelectedFile = (file: File) => {
    if (file.size > MAX_FILE_SIZE) {
      showFileError('File size exceeds 25MB limit');
      return;
    }

    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      showFileError('Only images and PDFs are allowed');
      return;
    }

    setUploadFile(file);
  };

  const handleDownload = async (doc: BankerDocument) => {
    if (!doc.filePath) return;

    try {
      const response = await fetch(`${doc.filePath}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.originalName || doc.docName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      showFileError('Download failed');
    }
  };

  const handleUploadClick = (doc: BankerDocument) => {
    setSelectedDoc(doc);
    setUploadDialogOpen(true);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      event.target.value = '';
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);

    const file = event.dataTransfer.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !selectedDoc) return;

    setUploading(true);
    try {
      await uploadCustomerDocument(selectedDoc.id, uploadFile);
      await fetchDocuments();

      setSnackbar({
        open: true,
        message: 'Document uploaded successfully',
        severity: 'success',
      });
      setUploadDialogOpen(false);
      setUploadFile(null);
      setSelectedDoc(null);
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.message || 'Failed to upload document',
        severity: 'error',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleCloseDialog = () => {
    setUploadDialogOpen(false);
    setUploadFile(null);
    setSelectedDoc(null);
    setIsDragOver(false);
  };

  const handleViewTermSheet = () => {
    onViewTermSheet(bankerId);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <>
      <Card
        sx={{
          mx: 'auto',
          mt: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          boxShadow: '0 10px 30px rgba(15,23,42,0.08)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <Box
            sx={{
              p: { xs: 2, sm: 2.5 },
              bgcolor: '#f8fafc',
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: 2,
              flexDirection: { xs: 'column', sm: 'row' },
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 800, overflowWrap: 'anywhere' }}>
                Documents from {bankerName}
              </Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                <Chip size="small" color="primary" label={`${documents.length} total`} sx={{ fontWeight: 700 }} />
                <Chip size="small" color="warning" variant="outlined" label={`${requestedCount} requested`} sx={{ fontWeight: 700 }} />
                <Chip size="small" color="success" variant="outlined" label={`${completedCount} uploaded`} sx={{ fontWeight: 700 }} />
                {bankerFileCount > 0 && (
                  <Chip size="small" variant="outlined" label={`${bankerFileCount} banker file${bankerFileCount === 1 ? '' : 's'}`} sx={{ fontWeight: 700 }} />
                )}
              </Stack>
            </Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={<VisibilityIcon />}
              onClick={handleViewTermSheet}
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                fontWeight: 700,
                alignSelf: { xs: 'stretch', sm: 'center' },
              }}
            >
              Term Sheet
            </Button>
          </Box>

          {documents.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <DescriptionIcon sx={{ color: 'text.secondary', fontSize: 38, mb: 1 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                No documents yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Banker files and upload requests will appear here.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Stack spacing={1.5}>
                {documents.map((doc) => (
                  <Box
                    key={doc.id}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                      p: { xs: 1.5, sm: 2 },
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) auto' },
                      gap: 2,
                      alignItems: 'center',
                      transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                      '&:hover': {
                        borderColor: 'primary.main',
                        boxShadow: '0 8px 20px rgba(15,23,42,0.08)',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 0 }}>
                      <Box
                        sx={{
                          width: 38,
                          height: 38,
                          borderRadius: 2,
                          bgcolor: doc.customerFilePath ? '#ecfdf5' : '#eff6ff',
                          color: doc.customerFilePath ? 'success.main' : 'primary.main',
                          display: 'grid',
                          placeItems: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <DescriptionIcon fontSize="small" />
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, overflowWrap: 'anywhere' }}>
                          {doc.docName}
                        </Typography>
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 0.75 }}>
                          {doc.isCustomerUpload && (
                            <Chip label="Requested" size="small" variant="outlined" color="warning" sx={{ fontWeight: 700 }} />
                          )}
                          {doc.filePath && (
                            <Chip label="Download, fill, upload" size="small" color="success" variant="outlined" sx={{ fontWeight: 700 }} />
                          )}
                          {doc.customerFilePath && (
                            <Chip label="Customer uploaded" size="small" color="success" sx={{ fontWeight: 700 }} />
                          )}
                        </Stack>
                        <Stack spacing={0.25} sx={{ mt: 1 }}>
                          {doc.originalName && (
                            <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                              Banker file: {doc.originalName}
                            </Typography>
                          )}
                          {doc.customerOriginalName && (
                            <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                              Customer file: {doc.customerOriginalName}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            Added {new Date(doc.createdAt).toLocaleDateString()} at {new Date(doc.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                        </Stack>
                      </Box>
                    </Box>

                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                      {doc.filePath && (
                        <Tooltip title="Download banker document">
                          <IconButton
                            onClick={() => handleDownload(doc)}
                            size="small"
                            sx={{ border: '1px solid', borderColor: 'divider' }}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {doc.customerFilePath && (
                        <>
                          <Tooltip title="Preview uploaded document">
                            <IconButton
                              onClick={() => {
                                if (doc.customerFilePath) {
                                  window.open(`${doc.customerFilePath}`, '_blank', 'noopener,noreferrer');
                                }
                              }}
                              size="small"
                              sx={{ border: '1px solid', borderColor: 'divider' }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {canCustomerUpload && (
                            <Tooltip title="Replace uploaded document">
                              <IconButton
                                onClick={() => handleUploadClick(doc)}
                                size="small"
                                sx={{ border: '1px solid', borderColor: 'divider' }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </>
                      )}
                      {!doc.customerFilePath && canCustomerUpload && (
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<UploadIcon />}
                          onClick={() => handleUploadClick(doc)}
                          sx={{
                            textTransform: 'none',
                            borderRadius: 2,
                            fontWeight: 700,
                            boxShadow: 'none',
                          }}
                        >
                          Upload
                        </Button>
                      )}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>

      <Dialog open={uploadDialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#f8fafc', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary', overflowWrap: 'anywhere' }}>
                {selectedDoc?.customerFilePath ? 'Replace Document' : 'Upload Document'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, overflowWrap: 'anywhere' }}>
                {selectedDoc?.docName}
              </Typography>
            </Box>
            <IconButton onClick={handleCloseDialog} disabled={uploading} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box
            sx={{
              mt: 1,
              border: '2px dashed',
              borderColor: isDragOver ? 'primary.main' : uploadFile ? 'success.main' : 'divider',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              bgcolor: uploadFile ? '#ecfdf5' : isDragOver ? '#eff6ff' : '#f8fafc',
              transition: 'background-color 0.2s ease, border-color 0.2s ease',
              cursor: 'pointer',
              '&:hover': {
                bgcolor: '#eff6ff',
                borderColor: 'primary.main',
              },
            }}
            onClick={() => document.getElementById('file-input')?.click()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <CloudUploadIcon sx={{ fontSize: 48, color: uploadFile ? 'success.main' : 'primary.main', mb: 2 }} />
            <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 800 }}>
              {uploadFile ? 'File selected' : isDragOver ? 'Drop file here' : 'Click to select or drag and drop'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: uploadFile ? 2 : 0 }}>
              Max 25MB. Images or PDFs only.
            </Typography>
            {uploadFile && (
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ minWidth: 0 }}>
                <DescriptionIcon sx={{ color: 'success.main' }} />
                <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 700, overflowWrap: 'anywhere' }}>
                  {uploadFile.name}
                </Typography>
              </Stack>
            )}
            <TextField
              id="file-input"
              type="file"
              inputProps={{ accept: 'image/*,application/pdf' }}
              onChange={handleFileChange}
              sx={{ display: 'none' }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'space-between' }}>
          <Button
            onClick={handleCloseDialog}
            disabled={uploading}
            variant="outlined"
            sx={{
              borderRadius: 2,
              fontWeight: 700,
              textTransform: 'none',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={!uploadFile || uploading}
            startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}
            sx={{
              borderRadius: 2,
              fontWeight: 700,
              textTransform: 'none',
              boxShadow: 'none',
            }}
          >
            {uploading ? 'Uploading...' : selectedDoc?.customerFilePath ? 'Replace Document' : 'Upload Document'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default BankerDocumentsView;
